import { useEffect, useRef, useState } from "react";
import Modal from "bootstrap/js/dist/modal";
import { CiCirclePlus } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";
import CreateDelegationModal from "../../../components/Modal/DelegationModal/CreateDelegationModal/CreateDelegationModal";
import CreateEmployeeModal from "../../../components/Modal/EmployeeModal/CreateEmployeeModal/CreateEmployeeModal";
import ConfirmationModal from "../../../components/reusableComps/ConfirmationModal/ConfirmationModal";
import FilterModal from "../../../components/reusableComps/FilterModal/FilterModal";
import SearchBar from "../../../components/reusableComps/SearchBar/SearchBar";
import DelegacoesTable from "../../../components/tables/DelegationTable/DelegationTable";
import FuncionariosTable from "../../../components/tables/EmployeeTable/EmployeeTable";
import EquipasTable from "../../../components/tables/TeamTable/TeamTable";
import { deleteDelegation, fetchDelegations } from "../../../redux/slices/delegationSlice";
import { deleteProject } from "../../../redux/slices/projectsSlice";
import { deleteUser, fetchUsers } from "../../../redux/slices/userSlice";

const OrganizationAdmin = () => {
  const [activeTab, setActiveTab] = useState("employees");
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();

  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const users = useSelector((state) => state.users.items || []);
  const delegations = useSelector((state) => state.delegations?.items || []);
  const managers = users ? users.filter((u) => u.role_id === 2) : [];
  const teams = useSelector((state) => state.teams?.items || []);

  const roleMap = {
    1: "SuperAdmin",
    2: "Manager",
    3: "Worker",
  };

  const [filters, setFilters] = useState({
    role: [],
    hiringDate: "",
    delegation: [],
    manager: [],
    createdAt: "",
    teamCreatedAt: "",
    team: [],
  });

  const createModalRef = useRef(null);
  const bootstrapModalInstance = useRef(null);

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchDelegations());
  }, [dispatch]);

  useEffect(() => {
    const modalElement = createModalRef.current;

    if (showDelegationModal && modalElement) {
      const modal = Modal.getOrCreateInstance(modalElement);
      bootstrapModalInstance.current = modal;

      const handleHidden = () => {
        setShowDelegationModal(false);
      };

      modalElement.addEventListener("hidden.bs.modal", handleHidden);
      modal.show();

      return () => {
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
      };
    }
  }, [showDelegationModal]);

  const filteredEmployees = users
    ?.filter((user) => {
      if (filters.role.length > 0 && !filters.role.includes(roleMap[user.role_id])) return false;
      if (filters.delegation.length > 0 && !filters.delegation.includes(user.delegation?.name)) return false;
      if (filters.manager.length > 0 && !filters.manager.includes(user.manager?.username)) return false;
      if (filters.status && user.status !== filters.status) return false;
      return true;
    })
    .filter((user) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        (user.username || "").toLowerCase().includes(search) ||
        (user.name || "").toLowerCase().includes(search)
      );
    });

  const sortedEmployees = filteredEmployees
    ? [...filteredEmployees].sort((a, b) => {
      if (filters.hiringDate === "Mais antiga") {
        return new Date(b.hiring_date) - new Date(a.hiring_date);
      }
      if (filters.hiringDate === "Mais recente") {
        return new Date(a.hiring_date) - new Date(b.hiring_date);
      }
      return 0;
    })
    : [];

  const userFilterConfig = [
    {
      name: "role",
      label: "Cargo",
      type: "select",
      options: ["SuperAdmin", "Manager", "Worker"],
      value: [],
    },
    {
      name: "hiringDate",
      label: "Data de Contratação",
      type: "select",
      options: ["Mais antiga", "Mais recente"],
      value: [],
    },
    {
      name: "delegation",
      label: "Delegação",
      type: "select",
      options: delegations.map((d) => d.name),
      value: [],
    },
    {
      name: "manager",
      label: "Manager",
      type: "select",
      options: managers.map((m) => m.username),
      value: [],
    },
  ];

  const delegationFilterConfig = [
    {
      name: "createdAt",
      label: "Data de Criação",
      type: "select",
      options: ["Criação mais antiga", "Criação mais recente"],
      value: [],
    },
    {
      name: "delegation",
      label: "Delegação",
      type: "select",
      options: delegations.map((d) => d.name),
      value: [],
    },
  ];

  const teamFilterConfig = [
    {
      name: "teamCreatedAt",
      label: "Data de Criação",
      type: "select",
      options: ["Criação mais antiga", "Criação mais recente"],
      value: [],
    },
    {
      name: "team",
      label: "Equipa",
      type: "select",
      options: teams.map((t) => t.name),
      value: [],
    },
  ];

  const handleApplyFilters = (appliedFilters) => {
    const hiringDateValue = Array.isArray(appliedFilters.hiringDate) ? (appliedFilters.hiringDate[0] || "") : appliedFilters.hiringDate;
    const createdAtValue = Array.isArray(appliedFilters.createdAt) ? (appliedFilters.createdAt[0] || "") : appliedFilters.createdAt;
    const teamCreatedAtValue = Array.isArray(appliedFilters.teamCreatedAt) ? (appliedFilters.teamCreatedAt[0] || "") : appliedFilters.teamCreatedAt;
    const teamValue = appliedFilters.team || [];
    const delegationValue = Array.isArray(appliedFilters.delegation) ? appliedFilters.delegation : [];
    const managerValue = Array.isArray(appliedFilters.manager) ? appliedFilters.manager : [];
    const roleValue = Array.isArray(appliedFilters.role) ? appliedFilters.role : [];
    setFilters({
      ...appliedFilters,
      hiringDate: hiringDateValue,
      createdAt: createdAtValue || "",
      teamCreatedAt: teamCreatedAtValue || "",
      team: teamValue,
      delegation: delegationValue,
      manager: managerValue,
      role: roleValue,
    });
  };

  let filteredDelegations = [...delegations];

  if (filters.createdAt === "Criação mais antiga") {
    filteredDelegations.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } else if (filters.createdAt === "Criação mais recente") {
    filteredDelegations.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  filteredDelegations = filteredDelegations.filter((delegation) => {
    if (filters.delegation.length === 0) return true;
    return filters.delegation.includes(delegation.name);
  });

  let filteredTeams = [...teams];

  if (filters.teamCreatedAt === "Criação mais antiga") {
    filteredTeams.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } else if (filters.teamCreatedAt === "Criação mais recente") {
    filteredTeams.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }

  filteredTeams = filteredTeams.filter((team) => {
    if (filters.team.length === 0) return true;
    return filters.team.includes(team.name);
  });

  const renderActiveTable = () => {
    switch (activeTab) {
      case "delegations":
        return <DelegacoesTable onSelect={handleSelectItem} searchTerm={searchTerm} delegations={filteredDelegations} />;
      case "teams":
        return <EquipasTable onSelect={handleSelectItem} searchTerm={searchTerm} teams={filteredTeams} />;
      default:
        return (
          <FuncionariosTable
            onSelect={handleSelectItem}
            searchTerm={searchTerm}
            employees={sortedEmployees}
          />
        );
    }
  };

  const handleSelectItem = (id, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, id]);
    } else {
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleDelete = () => {
    if (activeTab === "employees") {
      selectedItems.forEach((userId) => {
        dispatch(deleteUser(userId));
      });
    } else if (activeTab === "delegations") {
      selectedItems.forEach((delegationId) => {
        dispatch(deleteDelegation(delegationId));
      });
    } else if (activeTab === "teams") {
      selectedItems.forEach((teamId) => {
        dispatch(deleteProject(teamId));
      });
    }
    setSelectedItems([]);
  };

  const handleAddClick = () => {
    if (activeTab === "employees") {
      setShowEmployeeModal(true);
    } else if (activeTab === "delegations") {
      setShowDelegationModal(true);
    } else if (activeTab === "teams") {
      setShowTeamModal(true);
    }
  };

  const getButtonText = () => {
    if (activeTab === "employees") return "Adicionar Funcionário";
    if (activeTab === "delegations") return "Adicionar Delegação";
    if (activeTab === "teams") return "Adicionar Equipa";
    return "";
  };

  return (
    <div className="container position-relative pt-3">
      <style>{`
        body {
          background-color: #1B0F5B !important;
        }

        .card .table td,
        .card .table th {
          background-color: transparent !important;
          border-color: transparent;
          color: white;
        }

        .nav-tabs .nav-link {
          background-color: #121843;
          border: 1px solid transparent;
          border-radius: 0.5rem 0.5rem 0 0;
          color: rgb(255, 255, 255);
          padding: 0.5rem 1rem;
          margin-right: 5px;
          transition: background-color 0.3s ease;
        }

        .nav-tabs .nav-link:hover {
          background-color: #0E46A3;
        }

        .nav-tabs .nav-link.active {
          background-color: #0E46A3;
          color: white;
          border-color: #0d6efd #0d6efd #fff;
        }

      `}</style>

      <div className="d-flex justify-content-between align-items-center me-3 mb-3">
        <h2 className="text-white mb-0 fs-1 ms-3">Organização</h2>
        <div className="d-flex align-items-center">
          <div className="me-2">
            <FilterModal
              filterConfig={
                activeTab === "delegations"
                  ? delegationFilterConfig
                  : activeTab === "teams"
                    ? teamFilterConfig
                    : userFilterConfig
              }
              onApplyFilters={handleApplyFilters}
            />
          </div>
          <div style={{ width: "300px" }}>
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary py-1 ms-2"
            onClick={handleAddClick}
          >
            <CiCirclePlus style={{ height: "30px", width: "30px" }} className="me-1" />
            {getButtonText()}
          </button>
          {selectedItems.length > 0 && (
            <>
              <button
                className="btn btn-danger ms-2"
                onClick={() => {
                  setShowConfirmModal(true);
                }}
              >
                Eliminar
              </button>
              <ConfirmationModal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                onConfirm={() => {
                  handleDelete();
                  setShowConfirmModal(false);
                }}
                title="Confirmação"
                message={
                  activeTab === "delegations"
                    ? "Tem a certeza que deseja eliminar as delegações selecionadas?"
                    : activeTab === "teams"
                      ? "Tem a certeza que deseja eliminar as equipas selecionadas?"
                      : "Tem a certeza que deseja eliminar os funcionários selecionados?"
                }
              />
            </>
          )}
        </div>
      </div>

      <div className="card shadow-lg rounded-4 mt-3">
        <div className="card-body">
          <ul className="nav nav-tabs mb-3" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "employees" ? "active" : ""}`}
                onClick={() => setActiveTab("employees")}
                type="button"
                role="tab"
              >
                Funcionários
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "delegations" ? "active" : ""}`}
                onClick={() => setActiveTab("delegations")}
                type="button"
                role="tab"
              >
                Delegações
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === "teams" ? "active" : ""}`}
                onClick={() => setActiveTab("teams")}
                type="button"
                role="tab"
              >
                Equipas
              </button>
            </li>
          </ul>

          <div>{renderActiveTable()}</div>

          {showEmployeeModal && (
            <CreateEmployeeModal
              show={showEmployeeModal}
              onClose={() => setShowEmployeeModal(false)}
              onUserCreated={() => {
                setShowEmployeeModal(false);
              }}
            />
          )}

          <div
            className="modal fade"
            id="createDelegationModal"
            tabIndex="-1"
            aria-labelledby="createDelegationModalLabel"
            aria-hidden="true"
            ref={createModalRef}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div
                className="modal-content text-white"
                style={{ backgroundColor: "#17296B" }}
              >
                {showDelegationModal && (
                  <CreateDelegationModal
                    show={showDelegationModal}
                    onClose={() => {
                      if (bootstrapModalInstance.current) {
                        bootstrapModalInstance.current.hide();
                      } else {
                        setShowDelegationModal(false);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {showTeamModal && (
            <createProject
              // eslint-disable-next-line react/no-unknown-property
              show={showTeamModal}
              // eslint-disable-next-line react/no-unknown-property
              onClose={() => setShowTeamModal(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationAdmin;
